import { Box, Paper, Typography, useTheme, Stack, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuClipboardCheck, LuArrowLeft, LuBox, LuFolders, LuBuilding, LuLayoutTemplate } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../authentication/AuthProvider";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Product } from "../../db_access/product";
import { PendingValidation } from "../../db_access/pending_validation";
import routing from "../../routing";
import ValidationGrid from "./ValidationGrid";
import ValidationCenterDashboard from "./ValidationCenterDashboard";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import RefreshButton from "../../components/button/RefreshButton";
import MetricCard from "../../app_components/MetricCard";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const ValidationDashboard = () => {
    const { t } = useTranslation();
    const { isAdmin } = useAuth();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);
    const navigate = useNavigate();
    const { setRefresh, refresh } = useContextProvider();

    // View State: 'dashboard' or specific category key
    const [currentView, setCurrentView] = useState('dashboard');
    const [templates, setTemplates] = useState([]);

    useEffect(() => {
        if (!isAdmin) {
            navigate(routing.home);
        }
    }, [isAdmin, navigate]);

    useEffect(() => {
        PendingValidation.get_all()
            .then((res) => res.json())
            .then((data) => setTemplates(data.map((item) => {
                const mapped = { ...item };
                if (item.record_type === 'product') mapped.product_id = item.id;
                else if (item.record_type === 'project') mapped.project_id = item.id;
                else if (item.record_type === 'building') mapped.building_id = item.id;
                else if (item.record_type === 'organization' || item.record_type === 'structure' || item.record_type === 'supply_chain') {
                    mapped.organization_id = item.id;
                }
                return mapped;
            })));
    }, [refresh]);

    // Calculate statistics
    const stats = useMemo(() => {
        const total = templates.length;
        const validated = templates.filter(t => t.validation_status === 'Validated').length;
        const pending = templates.filter(t => t.validation_status === 'Pending').length;
        const not_validated = templates.filter(t => t.validation_status === 'Not Validated').length;

        return {
            total,
            validated,
            pending,
            not_validated,
            product: templates.filter(t => t.record_type === 'product').length,
            project: templates.filter(t => t.record_type === 'project').length,
            building: templates.filter(t => t.record_type === 'building').length,
            organization: templates.filter(t => t.record_type === 'organization' || t.record_type === 'structure' || t.record_type === 'supply_chain').length,
        };
    }, [templates]);

    const viewTitles = {
        product: 'Product Validation',
        project: 'Project Validation',
        building: 'Building Validation',
        organization: 'Organization Validation'
    };

    const topMetrics = useMemo(() => [
        { id: 'product', title: t("Product"), value: stats.product, unit: t("pending"), icon: LuBox, type: 'chain', accent: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
        { id: 'project', title: t("Project"), value: stats.project, unit: t("pending"), icon: LuFolders, type: 'scope3', accent: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
        { id: 'building', title: t("Building"), value: stats.building, unit: t("pending"), icon: LuBuilding, type: 'scope2', accent: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
        { id: 'organization', title: t("Organization"), value: stats.organization, unit: t("pending"), icon: LuLayoutTemplate, type: 'shield', accent: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' }
    ], [stats, t]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: tok.spacing.pageGap, animation: 'fadeIn 0.6s ease-out' }}>
            
            {/* 1. Standardized Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: tok.sectionHeading.titlePb,
                borderBottom: tok.sectionHeading.borderBottom
            }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    {currentView !== 'dashboard' && (
                        <Button
                            onClick={() => setCurrentView('dashboard')}
                            sx={{
                                minWidth: 40,
                                height: 40,
                                borderRadius: tok.radius.button,
                                borderColor: tok.border.card,
                                color: tok.text.primary,
                                '&:hover': { bgcolor: tok.bg.tabActive, borderColor: tok.bg.tabActive, color: '#fff' }
                            }}
                            variant="outlined"
                        >
                            <LuArrowLeft size={20} />
                        </Button>
                    )}
                    <Typography sx={{
                        ...tok.sectionHeading,
                        borderLeft: `${tok.sectionHeading.barWidth} solid ${tok.sectionHeading.barColor}`,
                        pl: 1,
                        borderBottom: 'none',
                        textAlign: 'left'
                    }}>
                        {currentView === 'dashboard' ? t("Validation Management") : t(viewTitles[currentView])}
                    </Typography>
                </Stack>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <RefreshButton onClick={() => setRefresh(r => !r)} />
                </Box>
            </Box>

            {/* 2. Metrics Row - Only visible in Dashboard View */}
            {currentView === 'dashboard' && (
                <Box sx={{ display: 'flex', gap: tok.spacing.metricCardGap, flexWrap: 'wrap', width: '100%', mb: 1 }}>
                    {topMetrics.map((m, i) => (
                        <Box key={m.id} sx={{ flex: '1 1 0', minWidth: '240px' }}>
                            <MetricCard
                                title={m.title}
                                value={m.value}
                                unit={m.unit}
                                icon={m.icon}
                                type={m.type}
                                bg={m.bg}
                                delay={i * 0.1}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            {/* 3. Main Content Area */}
            <Box sx={{ mt: 1 }}>
                {currentView === 'dashboard' ? (
                    <ValidationCenterDashboard onSelectCategory={setCurrentView} stats={stats} />
                ) : (
                    <Box sx={{ animation: 'fadeIn 0.4s ease' }}>
                        <ValidationGrid type={currentView} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ValidationDashboard;
